import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Globe, MapPin, Award, UserCircle2, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useLang } from '../context/LanguageContext';

const LANGUAGE_OPTIONS = ['English', 'French', 'Spanish', 'Vietnamese', 'Mandarin', 'Portuguese'];

export default function SpecialistsDirectoryPage() {
  const { t } = useLang();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    api.publicListSpecialists().then((rows) => setAll(rows || [])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((s) => !language
        || (s.languages || []).map((l) => (l || '').toLowerCase()).includes(language.toLowerCase()))
      .filter((s) => !q
        || (s.name || '').toLowerCase().includes(q)
        || (s.title || '').toLowerCase().includes(q)
        || (s.bio || '').toLowerCase().includes(q));
  }, [all, language, query]);

  return (
    <>
      <Header />
      <section className="py-14 bg-gradient-to-b from-[#fbf7ee] to-white border-b border-neutral-100">
        <div className="max-w-[1100px] mx-auto px-6">
          <p className="section-eyebrow mb-3">{t('spec.eyebrow')}</p>
          <h1 className="text-[36px] sm:text-[42px] lg:text-[52px] font-extrabold text-[#14110f] leading-[1.05] max-w-3xl">
            {t('spec.heroTitle')} <span className="text-[#f5b300]">{t('spec.heroAccent')}</span>.
          </h1>
          <p className="mt-4 text-[15px] text-neutral-600 max-w-2xl">
            {t('spec.heroSubtitle')}
          </p>

          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1 md:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="directory-search"
                     value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder={t('spec.searchPlaceholder')}
                     className="w-full pl-9 pr-3 py-3 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none bg-white" />
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="directory-language-filter">
              <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />{t('spec.languageLabel')}
              </span>
              {['', ...LANGUAGE_OPTIONS].map((l) => (
                <button key={l || 'any'} data-testid={`lang-filter-${l || 'any'}`}
                        onClick={() => setLanguage(l)}
                        className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition ${
                          language === l
                            ? 'bg-[#14110f] text-white'
                            : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#f5b300]'
                        }`}>
                  {l || t('spec.langAny')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[260px] rounded-xl bg-neutral-100 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <UserCircle2 className="w-12 h-12 mx-auto text-neutral-200" />
              <p className="mt-3 text-neutral-500 text-[14px]">{t('spec.empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                 data-testid="directory-grid">
              {filtered.map((s) => (
                <Link key={s.id} to={`/specialists/${s.slug}`}
                      data-testid={`directory-card-${s.slug}`}
                      className="group rounded-xl border border-neutral-200 bg-white p-5 hover:border-[#f5b300] hover:shadow-lg transition flex flex-col">
                  <div className="flex items-start gap-3">
                    {s.profile_picture_url ? (
                      <img src={s.profile_picture_url} alt={s.name}
                           className="w-14 h-14 rounded-full object-cover border border-neutral-100" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-[#fff8e3] flex items-center justify-center">
                        <UserCircle2 className="w-9 h-9 text-[#c89200]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-bold text-[#14110f] text-[15px] truncate">{s.name}</p>
                      <p className="text-[12px] text-neutral-500 truncate">{s.title || t('spec.titleFallback')}</p>
                    </div>
                  </div>
                  {s.bio && (
                    <p className="mt-3 text-[12.5px] text-neutral-600 leading-relaxed line-clamp-3">{s.bio}</p>
                  )}
                  <div className="mt-3 space-y-1 text-[11.5px] text-neutral-500">
                    {(s.languages || []).length > 0 && (
                      <p className="flex items-start gap-1.5">
                        <Globe className="w-3 h-3 mt-0.5 text-[#c89200] shrink-0" />
                        <span>{(s.languages || []).join(', ')}</span>
                      </p>
                    )}
                    {s.office_location && (
                      <p className="flex items-start gap-1.5">
                        <MapPin className="w-3 h-3 mt-0.5 text-[#c89200] shrink-0" />
                        <span>{s.office_location}</span>
                      </p>
                    )}
                    {s.certifications && (
                      <p className="flex items-start gap-1.5">
                        <Award className="w-3 h-3 mt-0.5 text-[#c89200] shrink-0" />
                        <span className="truncate">{s.certifications}</span>
                      </p>
                    )}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#c89200] group-hover:gap-2 transition-all">
                    {t('spec.viewProfile')} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
